import React, { useState } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { taskAPI, columnAPI } from '../lib/api';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Progress } from './ui/progress';
import { Checkbox } from './ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Label } from './ui/label';
import { Slider } from './ui/slider';
import { toast } from 'sonner';
import { formatDateShort, getProgressColor } from '../lib/utils';
import { Plus, MoreHorizontal, Trash2, GripVertical, Calendar, CheckSquare, X, Palette, Move, Search } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

// Predefined colors for tasks
const TASK_COLORS = [
  { name: 'Orange', value: '#f97316' },
  { name: 'Blue', value: '#3b82f6' },
  { name: 'Green', value: '#22c55e' },
  { name: 'Purple', value: '#a855f7' },
  { name: 'Pink', value: '#ec4899' },
  { name: 'Yellow', value: '#eab308' },
  { name: 'Cyan', value: '#06b6d4' },
  { name: 'Red', value: '#ef4444' },
  { name: 'Indigo', value: '#6366f1' },
  { name: 'Teal', value: '#14b8a6' },
];

function TaskCard({ task, onClick, selectionMode, isSelected, onSelect }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id, disabled: selectionMode });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: selectionMode ? 'pointer' : 'grab',
  };

  const handleCheckboxChange = (e) => {
    e.stopPropagation();
    onSelect(task.id);
  };

  const handleCardClick = (e) => {
    // Only trigger click if not dragging
    if (selectionMode) {
      onSelect(task.id);
    } else {
      onClick();
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...(selectionMode ? {} : { ...attributes, ...listeners })}
      className={`kanban-card group ${isSelected ? 'ring-2 ring-primary ring-offset-2' : ''} ${isDragging ? 'cursor-grabbing' : ''}`}
      data-testid={`kanban-card-${task.id}`}
    >
      <div className="flex items-start gap-2">
        {selectionMode ? (
          <div className="mt-1" onClick={handleCheckboxChange}>
            <Checkbox
              checked={isSelected}
              onCheckedChange={() => onSelect(task.id)}
              data-testid={`select-task-${task.id}`}
              className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
            />
          </div>
        ) : (
          <div className="mt-1 opacity-50 group-hover:opacity-100 transition-opacity">
            <GripVertical className="w-4 h-4 text-zinc-400" />
          </div>
        )}
        <div className="flex-1 min-w-0" onClick={handleCardClick}>
          <h4 className="font-medium text-zinc-900 text-sm mb-2 hover:text-primary transition-colors">
            {task.name}
          </h4>
          <div className="flex items-center gap-2 mb-3">
            <Calendar className="w-3 h-3 text-zinc-400" />
            <span className="text-xs text-zinc-500">
              {formatDateShort(task.start_date)} - {formatDateShort(task.end_date)}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Progress value={task.progress} className="h-1.5 flex-1" />
            <span className="text-xs text-zinc-500">{task.progress}%</span>
          </div>
        </div>
      </div>
      <div
        className="absolute top-0 left-0 w-1 h-full rounded-l-xl"
        style={{ backgroundColor: task.color || '#f97316' }}
      />
    </div>
  );
}

function KanbanColumn({ column, tasks, onAddTask, onDeleteColumn, onTaskClick, selectionMode, selectedTasks, onSelectTask }) {
  const [isAdding, setIsAdding] = useState(false);
  const [newTaskName, setNewTaskName] = useState('');
  
  // Make column droppable
  const { setNodeRef, isOver } = useDroppable({
    id: column.id,
  });

  const handleAddTask = () => {
    if (newTaskName.trim()) {
      onAddTask(column.id, newTaskName);
      setNewTaskName('');
      setIsAdding(false);
    }
  };

  return (
    <div className="flex-shrink-0 w-80" data-testid={`kanban-column-${column.id}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h3 className="font-outfit font-semibold text-zinc-900">{column.name}</h3>
          <span className="text-xs bg-zinc-100 text-zinc-600 px-2 py-0.5 rounded-full">
            {tasks.length}
          </span>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              <MoreHorizontal className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={() => onDeleteColumn(column.id)}
              className="text-red-600"
              data-testid={`delete-column-${column.id}`}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Column
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div 
        ref={setNodeRef}
        className={`bg-zinc-50 rounded-xl p-3 min-h-[200px] transition-colors ${isOver ? 'bg-orange-50 ring-2 ring-orange-300' : ''}`}
      >
        <SortableContext items={tasks.map(t => t.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-3">
            {tasks.map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                onClick={() => onTaskClick(task)}
                selectionMode={selectionMode}
                isSelected={selectedTasks.includes(task.id)}
                onSelect={onSelectTask}
              />
            ))}
          </div>
        </SortableContext>

        {isAdding ? (
          <div className="mt-3 p-3 bg-white rounded-xl border border-zinc-200">
            <Input
              value={newTaskName}
              onChange={(e) => setNewTaskName(e.target.value)}
              placeholder="Task name"
              className="mb-2 rounded-lg"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAddTask();
                if (e.key === 'Escape') setIsAdding(false);
              }}
              data-testid={`new-task-input-${column.id}`}
            />
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={handleAddTask}
                data-testid={`add-task-confirm-${column.id}`}
                className="bg-primary hover:bg-orange-600 text-white rounded-lg"
              >
                Add
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setIsAdding(false);
                  setNewTaskName('');
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <Button
            variant="ghost"
            className="w-full mt-3 text-zinc-500 hover:text-zinc-900 justify-start"
            onClick={() => setIsAdding(true)}
            data-testid={`add-task-btn-${column.id}`}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Task
          </Button>
        )}
      </div>
    </div>
  );
}

export default function KanbanBoard({ projectId, tasks, columns, onTaskUpdate, onColumnsUpdate, onRefresh }) {
  const [editTask, setEditTask] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [newColumnName, setNewColumnName] = useState('');
  const [addingColumn, setAddingColumn] = useState(false);
  const [activeTask, setActiveTask] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Bulk selection state
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [bulkColorOpen, setBulkColorOpen] = useState(false);
  const [bulkMoveOpen, setBulkMoveOpen] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Filter tasks by search query
  const filteredTasks = searchQuery.trim() 
    ? tasks.filter(t => t.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : tasks;

  const getTasksByColumn = (columnId) => {
    return filteredTasks.filter(t => t.column_id === columnId).sort((a, b) => a.order - b.order);
  };

  const handleDragStart = (event) => {
    if (selectionMode) return;
    const task = tasks.find(t => t.id === event.active.id);
    setActiveTask(task);
  };

  const handleDragEnd = async (event) => {
    if (selectionMode) return;
    const { active, over } = event;
    setActiveTask(null);

    if (!over) return;

    const activeTask = tasks.find(t => t.id === active.id);
    if (!activeTask) return;

    // Find target column
    let targetColumnId = null;
    const overTask = tasks.find(t => t.id === over.id);
    
    if (overTask) {
      targetColumnId = overTask.column_id;
    } else {
      // Dropped on column itself
      targetColumnId = columns.find(c => c.id === over.id)?.id;
    }

    if (!targetColumnId) return;

    // If column changed, update task
    if (activeTask.column_id !== targetColumnId) {
      try {
        const res = await taskAPI.update(projectId, activeTask.id, {
          column_id: targetColumnId,
        });
        onTaskUpdate(res.data);
        toast.success('Task moved');
      } catch (error) {
        toast.error('Failed to move task');
      }
    }
  };

  // Bulk selection handlers
  const handleSelectTask = (taskId) => {
    setSelectedTasks(prev => 
      prev.includes(taskId) 
        ? prev.filter(id => id !== taskId)
        : [...prev, taskId]
    );
  };

  const handleSelectAll = () => {
    if (selectedTasks.length === tasks.length) {
      setSelectedTasks([]);
    } else {
      setSelectedTasks(tasks.map(t => t.id));
    }
  };

  const handleCancelSelection = () => {
    setSelectionMode(false);
    setSelectedTasks([]);
    setBulkColorOpen(false);
    setBulkMoveOpen(false);
  };

  const handleBulkColorChange = async (color) => {
    if (selectedTasks.length === 0) return;
    
    setBulkActionLoading(true);
    try {
      await taskAPI.bulkUpdate(projectId, {
        task_ids: selectedTasks,
        color: color,
      });
      toast.success(`Updated color for ${selectedTasks.length} tasks`);
      onRefresh();
      setBulkColorOpen(false);
    } catch (error) {
      toast.error('Failed to update task colors');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkMoveColumn = async (columnId) => {
    if (selectedTasks.length === 0) return;
    
    setBulkActionLoading(true);
    try {
      await taskAPI.bulkUpdate(projectId, {
        task_ids: selectedTasks,
        column_id: columnId,
      });
      toast.success(`Moved ${selectedTasks.length} tasks`);
      onRefresh();
      setBulkMoveOpen(false);
    } catch (error) {
      toast.error('Failed to move tasks');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedTasks.length === 0) return;
    if (!window.confirm(`Delete ${selectedTasks.length} selected tasks? This cannot be undone.`)) return;
    
    setBulkActionLoading(true);
    try {
      await taskAPI.bulkDelete(projectId, selectedTasks);
      toast.success(`Deleted ${selectedTasks.length} tasks`);
      setSelectedTasks([]);
      onRefresh();
    } catch (error) {
      toast.error('Failed to delete tasks');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleAddTask = async (columnId, name) => {
    const today = new Date().toISOString().split('T')[0];
    const nextWeek = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

    try {
      await taskAPI.create(projectId, {
        name,
        start_date: today,
        end_date: nextWeek,
        column_id: columnId,
        progress: 0,
      });
      onRefresh();
      toast.success('Task added');
    } catch (error) {
      toast.error('Failed to add task');
    }
  };

  const handleAddColumn = async () => {
    if (!newColumnName.trim()) return;

    setAddingColumn(true);
    try {
      const res = await columnAPI.create(projectId, { name: newColumnName });
      onColumnsUpdate([...columns, res.data]);
      setNewColumnName('');
      toast.success('Column added');
    } catch (error) {
      toast.error('Failed to add column');
    } finally {
      setAddingColumn(false);
    }
  };

  const handleDeleteColumn = async (columnId) => {
    if (!window.confirm('Delete this column? Tasks will be moved to the first column.')) return;

    try {
      await columnAPI.delete(projectId, columnId);
      onRefresh();
      toast.success('Column deleted');
    } catch (error) {
      toast.error('Failed to delete column');
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditLoading(true);

    try {
      const res = await taskAPI.update(projectId, editTask.id, {
        name: editTask.name,
        progress: editTask.progress,
      });
      onTaskUpdate(res.data);
      setEditTask(null);
      toast.success('Task updated');
    } catch (error) {
      toast.error('Failed to update task');
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteTask = async () => {
    if (!editTask) return;

    try {
      await taskAPI.delete(projectId, editTask.id);
      onRefresh();
      setEditTask(null);
      toast.success('Task deleted');
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  return (
    <Card className="bg-white border-zinc-200 rounded-xl p-6" data-testid="kanban-board">
      {/* Header with search and bulk selection toggle */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <h3 className="font-outfit font-semibold text-zinc-900">Kanban Board</h3>
          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <Input
              placeholder="Search tasks..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 w-48 rounded-lg"
              data-testid="kanban-search-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          {searchQuery && (
            <span className="text-sm text-zinc-500">
              {filteredTasks.length} of {tasks.length} tasks
            </span>
          )}
          {tasks.length > 0 && !searchQuery && (
            <Button
              variant={selectionMode ? "default" : "outline"}
              size="sm"
              onClick={() => selectionMode ? handleCancelSelection() : setSelectionMode(true)}
              data-testid="toggle-selection-mode"
              className={selectionMode ? "bg-primary hover:bg-orange-600 text-white" : ""}
            >
              <CheckSquare className="w-4 h-4 mr-2" />
              {selectionMode ? 'Cancel Selection' : 'Select Multiple'}
            </Button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Input
            placeholder="New column name"
            value={newColumnName}
            onChange={(e) => setNewColumnName(e.target.value)}
            className="w-48 rounded-lg"
            onKeyDown={(e) => e.key === 'Enter' && handleAddColumn()}
            data-testid="new-column-input"
          />
          <Button
            onClick={handleAddColumn}
            disabled={addingColumn || !newColumnName.trim()}
            data-testid="add-column-btn"
            className="bg-primary hover:bg-orange-600 text-white rounded-lg"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Column
          </Button>
        </div>
      </div>

      {/* Bulk Actions Toolbar */}
      {selectionMode && (
        <div className="mb-6 p-4 bg-zinc-50 rounded-xl border border-zinc-200" data-testid="bulk-actions-toolbar">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={handleSelectAll}
                data-testid="select-all-btn"
              >
                {selectedTasks.length === tasks.length ? 'Deselect All' : 'Select All'}
              </Button>
              <span className="text-sm text-zinc-600">
                {selectedTasks.length} of {tasks.length} tasks selected
              </span>
            </div>
            
            {selectedTasks.length > 0 && (
              <div className="flex items-center gap-2">
                {/* Change Color */}
                <DropdownMenu open={bulkColorOpen} onOpenChange={setBulkColorOpen}>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={bulkActionLoading}
                      data-testid="bulk-change-color-btn"
                    >
                      <Palette className="w-4 h-4 mr-2" />
                      Change Color
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="p-3">
                    <div className="flex flex-wrap gap-2 max-w-[200px]">
                      {TASK_COLORS.map((color) => (
                        <button
                          key={color.value}
                          onClick={() => handleBulkColorChange(color.value)}
                          className="w-8 h-8 rounded-full transition-all hover:scale-110 hover:ring-2 hover:ring-offset-2 hover:ring-zinc-400"
                          style={{ backgroundColor: color.value }}
                          title={color.name}
                          data-testid={`bulk-color-${color.name.toLowerCase()}`}
                        />
                      ))}
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Move to Column */}
                <DropdownMenu open={bulkMoveOpen} onOpenChange={setBulkMoveOpen}>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={bulkActionLoading || columns.length === 0}
                      data-testid="bulk-move-btn"
                    >
                      <Move className="w-4 h-4 mr-2" />
                      Move to Column
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {columns.map((column) => (
                      <DropdownMenuItem
                        key={column.id}
                        onClick={() => handleBulkMoveColumn(column.id)}
                        data-testid={`bulk-move-to-${column.id}`}
                      >
                        {column.name}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Delete Selected */}
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleBulkDelete}
                  disabled={bulkActionLoading}
                  data-testid="bulk-delete-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete ({selectedTasks.length})
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="flex gap-6 overflow-x-auto pb-4">
          {columns.map((column) => (
            <KanbanColumn
              key={column.id}
              column={column}
              tasks={getTasksByColumn(column.id)}
              onAddTask={handleAddTask}
              onDeleteColumn={handleDeleteColumn}
              onTaskClick={setEditTask}
              selectionMode={selectionMode}
              selectedTasks={selectedTasks}
              onSelectTask={handleSelectTask}
            />
          ))}

          {columns.length === 0 && (
            <div className="flex-1 flex items-center justify-center py-20 text-zinc-500">
              No columns yet. Add a column to get started.
            </div>
          )}
        </div>

        <DragOverlay>
          {activeTask && (
            <div className="kanban-card shadow-lg rotate-2">
              <h4 className="font-medium text-zinc-900 text-sm">{activeTask.name}</h4>
            </div>
          )}
        </DragOverlay>
      </DndContext>

      {/* Edit Task Dialog */}
      <Dialog open={!!editTask} onOpenChange={() => setEditTask(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit">Edit Task</DialogTitle>
            <DialogDescription>Update task name and progress</DialogDescription>
          </DialogHeader>
          {editTask && (
            <form onSubmit={handleEditSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Task Name</Label>
                <Input
                  value={editTask.name}
                  onChange={(e) => setEditTask({ ...editTask, name: e.target.value })}
                  data-testid="edit-kanban-task-name"
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label>Progress: {editTask.progress}%</Label>
                <Slider
                  value={[editTask.progress]}
                  onValueChange={([val]) => setEditTask({ ...editTask, progress: val })}
                  max={100}
                  step={5}
                  data-testid="edit-kanban-task-progress"
                />
              </div>
              <div className="flex justify-between pt-4">
                <Button
                  type="button"
                  variant="destructive"
                  onClick={handleDeleteTask}
                  data-testid="delete-kanban-task-btn"
                  className="rounded-lg"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
                <div className="flex gap-3">
                  <Button type="button" variant="outline" onClick={() => setEditTask(null)} className="rounded-lg">
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={editLoading}
                    data-testid="save-kanban-task-btn"
                    className="bg-primary hover:bg-orange-600 text-white rounded-lg"
                  >
                    {editLoading ? <span className="spinner" /> : 'Save'}
                  </Button>
                </div>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
